<template>
  <SearchBoxCore
    ref="SearchBox"
    :label="label"
    :placeholder="placeholder"
    :items="items"
    :quantidade-total="quantidadeTotal"
    :pagina="pagina"
    :quantidade-por-pagina="quantidadePorPagina"
    :value="value"
    :is-loading="isLoading"
    :selected="selected"
    :incluir="incluir"
    :alterar="alterar"
    :tipo="tipo"
    :hidelabel="hidelabel"
    :disabled="disabled"
    :multiline="multiline"
    @alteracao="alteracao"
    @pressEnter="pressEnter"
    @selecionarItem="selecionarItem"
    @apagar="apagar"
    @voltarPagina="voltarPagina"
    @avancarPagina="avancarPagina"
    @botaoInclusao="$emit('botaoInclusao')"
    @botaoAlteracao="$emit('botaoAlteracao')"
  >
    <template #label>
      <slot name="label" />
    </template>
  </SearchBoxCore>
</template>

<script>
import SearchBoxCore from './SearchBoxCore.vue';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_DEBOUNCE_MS = 800;

export default {
  name: 'SearchBox',
  components: { SearchBoxCore },
  props: {
    funcaoBuscar: { type: String, required: true },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    incluir: { type: Boolean, default: false },
    alterar: { type: Boolean, default: false },
    tipo: { type: String, default: '' },
    hidelabel: { type: Boolean, default: false },
    hidedescricao: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    multiline: { type: Boolean, default: false },
    parametros: { type: Object, default: () => ({}) },
    debounceMs: { type: Number, default: DEFAULT_DEBOUNCE_MS },
    quantidadePorPaginaProp: { type: Number, default: DEFAULT_PAGE_SIZE }
  },
  data() {
    return {
      value: '',
      isLoading: false,
      timerBusca: null,
      pagina: 1,
      quantidadeTotal: 0,
      items: [],
      selected: {},
      searchRequestId: 0
    };
  },
  computed: {
    quantidadePorPagina() {
      return this.quantidadePorPaginaProp || DEFAULT_PAGE_SIZE;
    },
    hasValue() {
      return Boolean(String(this.value || '').trim());
    }
  },
  beforeDestroy() {
    this.cancelarTimerBusca();
    this.searchRequestId += 1;
  },
  methods: {
    cancelarTimerBusca() {
      if (this.timerBusca) {
        clearTimeout(this.timerBusca);
        this.timerBusca = null;
      }
    },
    buscarComDebounce() {
      this.cancelarTimerBusca();
      this.timerBusca = setTimeout(() => {
        this.realizarBuscar();
      }, this.debounceMs);
    },
    montarPayloadBusca() {
      return {
        ...this.parametros,
        NOME: this.value,
        PAGINA: this.pagina,
        QUANTIDADE: this.quantidadePorPagina
      };
    },
    normalizarItems(items = []) {
      if (!this.hidedescricao) return items;
      return items.map(({ ...item }) => item);
    },
    async realizarBuscar() {
      this.cancelarTimerBusca();

      const requestId = ++this.searchRequestId;
      this.isLoading = true;

      try {
        const retorno = await this.$store.dispatch(this.funcaoBuscar, this.montarPayloadBusca());

        if (requestId !== this.searchRequestId) return;

        const items = Array.isArray(retorno && retorno.ITEMS) ? retorno.ITEMS : [];
        this.items = this.normalizarItems(items);
        this.quantidadeTotal = Number(retorno && retorno.QUANTIDADE_TOTAL) || 0;

        this.$emit('buscado', {
          items: this.items,
          quantidadeTotal: this.quantidadeTotal,
          pagina: this.pagina,
          quantidadePorPagina: this.quantidadePorPagina
        });

        this.$nextTick(() => {
          if (this.$refs.SearchBox && this.hasValue) {
            this.$refs.SearchBox.colocarFoco();
          }
        });
      } catch (error) {
        if (requestId === this.searchRequestId) {
          this.items = [];
          this.quantidadeTotal = 0;
          this.$emit('erro', error);
        }
      } finally {
        if (requestId === this.searchRequestId) {
          this.isLoading = false;
        }
      }
    },
    alteracao(value) {
      const novoValor = value || '';
      const mudouValor = novoValor !== this.value;

      this.value = novoValor;
      this.$emit('alteracao', this.value);

      if (mudouValor) {
        this.selected = {};
        this.pagina = 1;
        this.quantidadeTotal = 0;
      }

      if (this.hasValue) {
        this.buscarComDebounce();
      } else {
        this.apagar(false);
      }
    },
    pressEnter() {
      this.realizarBuscar();
    },
    formatarDescricaoSelecionada(item) {
      if (!item) return '';
      if (this.hidedescricao) return item.ID || '';
      if (!item.DESCRICAO) return item.ID || '';

      const descricao = String(item.DESCRICAO)
        .replace(/<\/?b>/gi, '')
        .replace(/<br\s*\/?\s*>/gi, ' ')
        .trim();

      return item.ID ? `${descricao} - ${item.ID}` : descricao;
    },
    selecionarItem(index) {
      const item = this.items[index];
      if (!item) return;

      this.selected = item;
      this.items = [];
      this.quantidadeTotal = 0;
      this.value = this.formatarDescricaoSelecionada(item);
      this.$emit('selected', this.selected);
    },
    selecionarItemExterno(item = {}) {
      this.selected = item;
      this.items = [];
      this.quantidadeTotal = 0;
      this.value = this.formatarDescricaoSelecionada(item);
    },
    apagar(apagarValue = true) {
      this.cancelarTimerBusca();
      this.searchRequestId += 1;
      this.items = [];
      this.selected = {};
      this.pagina = 1;
      this.quantidadeTotal = 0;
      this.isLoading = false;

      if (apagarValue) {
        this.value = '';
      }

      this.$emit('selected', {});
    },
    voltarPagina() {
      if (this.pagina <= 1) return;
      this.pagina -= 1;
      this.realizarBuscar();
    },
    avancarPagina() {
      if (this.quantidadeTotal <= this.pagina * this.quantidadePorPagina) return;
      this.pagina += 1;
      this.realizarBuscar();
    },
    setFocus() {
      if (this.$refs.SearchBox) this.$refs.SearchBox.setFocus();
    }
  }
};
</script>
